import React from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import "../styles/lineage-article.css";

const reconciliationSteps = [
  {
    title: "Profile both sides",
    body:
      "Capture row counts, key uniqueness, null distribution, data types, date ranges and obvious anomalies before comparing values.",
  },
  {
    title: "Conform the shape",
    body:
      "Forward-engineer source datasets into the target model, then reverse-engineer target output into a conformed analytical shape.",
  },
  {
    title: "Compare by business key",
    body:
      "Join expected and actual datasets on stable business keys, not file order, row number or dashboard-level totals.",
  },
  {
    title: "Return evidence",
    body:
      "Output the failed key, failed column, expected value, actual value, tolerance rule and batch run that produced the mismatch.",
  },
];

const architectureLayers = [
  {
    layer: "Bronze",
    title: "Raw ingestion",
    body:
      "Source extracts, API payloads, files and database snapshots are landed with batch IDs, watermarks and load metadata.",
  },
  {
    layer: "Silver",
    title: "Conformed entities",
    body:
      "Business entities are cleaned, typed, deduplicated and shaped into datasets that can be tested consistently.",
  },
  {
    layer: "Gold",
    title: "Reporting model",
    body:
      "Analytics-ready fact and dimension models serve Power BI, operational reporting and downstream consumers.",
  },
  {
    layer: "Reconciliation",
    title: "Evidence layer",
    body:
      "Expected and actual outputs are compared row by row, with mismatch evidence stored for investigation and sign-off.",
  },
];

const sourceLinks = [
  {
    label: "Polars Lazy optimisations",
    href: "https://docs.pola.rs/user-guide/lazy/optimizations/",
  },
  {
    label: "Polars GPU support",
    href: "https://docs.pola.rs/user-guide/gpu-support/",
  },
  {
    label: "Polars Cloud architecture",
    href: "https://pola.rs/posts/polars-cloud-what-we-are-building/",
  },
  {
    label: "Polars benchmarks",
    href: "https://pola.rs/posts/benchmarks/",
  },
];

export default function MigrationTestingPolarsArticle() {
  return (
    <div className="site-page lineage-page blog-article-page">
      <Seo
        title="Testing Data Migrations at Scale with Python, Polars and Cloud Compute | Data Apparatus"
        description="A practical Data Apparatus article on row-level reconciliation, Python automation, Polars, distributed compute and medallion architecture for large data migration testing."
        canonicalPath="/blog/testing-data-migrations-python-polars"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Testing Data Migrations at Scale with Python, Polars and Cloud Compute",
          description:
            "A practical article on row-level reconciliation, Python automation, Polars, distributed compute and medallion architecture for large data migration testing.",
          author: {
            "@type": "Person",
            name: "Nurlan Alizada",
          },
          publisher: {
            "@type": "Organization",
            name: "Data Apparatus",
            url: "https://dataapparatus.com.au",
          },
          datePublished: "2026-06-28",
          mainEntityOfPage:
            "https://dataapparatus.com.au/blog/testing-data-migrations-python-polars",
        }}
      />

      <main className="blog-shell">
        <article className="blog-post">
          <header className="blog-header">
            <Link to="/blog" className="blog-back-link">
              Blog
            </Link>
            <p className="blog-kicker">Migration testing and scalable data engineering</p>
            <h1>Testing data migrations at scale with Python, Polars and cloud compute.</h1>
            <p className="blog-subtitle">
              Row counts are not enough. Migration teams need row-level evidence that
              shows exactly which records, fields and rules do not match.
            </p>
            <div className="blog-meta">
              <span>Nurlan Alizada</span>
              <span>Data Apparatus</span>
              <span>June 28, 2026</span>
              <span>11 min read</span>
            </div>
          </header>

          <p className="blog-lead">
            I have always used Python to automate the parts of data work that are too
            repetitive, too risky or too large to do manually. Migration testing is one of
            those areas.
          </p>

          <p>
            In many migration projects, teams compare row counts, aggregate totals or a few
            dashboard numbers and assume the migrated data is correct. That can be useful
            as a smoke test, but it is not enough evidence for a serious migration.
          </p>

          <p>
            If a system has been forward-engineered into a new model, and the new system
            output has been reverse-engineered back into a conformed analytical layer, often called
            the silver layer in medallion architecture, the test needs to prove
            equivalence at record and field level. The question is not
            only whether the total balance matches. The question is which customer,
            facility, transaction or account failed, which column failed, and whether the
            difference is expected.
          </p>

          <blockquote>
            When migration testing moves beyond row counts into row-level reconciliation,
            compute efficiency becomes part of data quality architecture.
          </blockquote>

          <h2>The reconciliation pattern</h2>

          <p>
            The pattern I like is simple in principle. Take the source dataset, transform it
            into the expected target shape, take the target-system output, transform it back
            into a comparable conformed shape, and then compare the two datasets by stable
            business keys.
          </p>

          <div className="blog-process-grid">
            {reconciliationSteps.map((step) => (
              <section key={step.title}>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </section>
            ))}
          </div>

          <p>
            This gives migration teams evidence. Not just "the totals look close", but
            "these are the records that failed, these are the columns that changed, and this
            is the batch that produced the issue."
          </p>

          <h2>Why large datasets change the testing strategy</h2>

          <p>
            Once datasets become large, testing itself becomes a data engineering problem.
            A Python script that works well on a small extract can become slow, expensive
            or memory-heavy when applied to millions or hundreds of millions of records.
          </p>

          <p>
            That is where Polars becomes interesting. Polars is a high-performance
            dataframe library written in Rust with a Python API. The practical benefit is
            that you can keep the ergonomics of Python while using an execution engine
            designed for efficient, columnar, parallel data processing.
          </p>

          <p>
            The Polars lazy engine can optimise a query plan before execution. Its official
            documentation describes optimisations such as predicate pushdown, projection
            pushdown, slice pushdown, join ordering, common subplan elimination and type
            coercion. For reconciliation workloads, these optimisations matter because the
            work often involves joins, filters, selected columns, type normalisation and
            grouped summaries.
          </p>

          <div className="blog-callout">
            <h3>The practical advantage</h3>
            <p>
              Polars is valuable because it lets data teams keep the speed of Python-based
              automation while moving heavy comparison work into an efficient execution
              engine. For migration testing, that means faster iteration, lower memory
              pressure and a cleaner path from local development to larger reconciliation
              workloads.
            </p>
          </div>

          <h2>Polars, Spark and the compute question</h2>

          <p>
            PySpark remains important when organisations already have a Spark platform,
            cluster governance, Delta Lake patterns, lakehouse standards and very large
            distributed workloads. But Spark also brings operational weight. It depends on
            cluster configuration, JVM behaviour, shuffle tuning, executor sizing and
            platform overhead.
          </p>

          <p>
            Polars takes a different path. It is designed to get a lot out of a single
            machine first, then scale into remote and distributed execution through Polars
            Cloud. That matters because many migration tests do not immediately need a
            large cluster. They need efficient local development, fast iteration and a path
            to scale when the dataset or workload grows.
          </p>

          <p>
            Polars benchmarks published by the project have shown strong results against
            commonly used dataframe and distributed tools on their tested PDS-H workloads.
            Benchmarks should always be read with care because hardware, query shape, file
            format and implementation details matter. But the direction is important:
            efficient compute is becoming a design choice, not an afterthought.
          </p>

          <h2>Why CPU efficiency matters first</h2>

          <p>
            For many migration testing workloads, the first performance problem is not the
            absence of GPUs. It is whether the available CPU and memory are being used
            efficiently. Row-level reconciliation can involve scanning large files,
            selecting a subset of columns, normalising types, joining on business keys and
            producing mismatch outputs. Those operations benefit directly from a fast,
            parallel, columnar execution engine.
          </p>

          <p>
            This is where Polars is compelling. Because the engine is written in Rust and
            designed for parallel execution, it can make strong use of the compute already
            available on a machine. For many practical testing and transformation jobs,
            better CPU utilisation, lower memory pressure and lazy query optimisation are
            more immediately useful than introducing another large distributed platform.
          </p>

          <p>
            GPU support is still worth watching, and Polars can dispatch supported
            LazyFrame workloads to NVIDIA GPUs through the RAPIDS cuDF backend. But I see
            that as an extension of the compute story, not the main point. The main point
            is that efficient engines let teams do more with the infrastructure they
            already have.
          </p>

          <h2>What changes in the cloud?</h2>

          <p>
            Polars Cloud is interesting because it tries to reduce the gap between local
            dataframe development and production-scale execution. The Polars team describes
            a model where a LazyFrame can be sent to remote compute, with configurable CPU
            and GPU hardware, distributed execution, serverless-style operation, and the
            ability to run close to data in cloud environments.
          </p>

          <p>
            For migration testing, that creates a useful architecture. You can design and
            validate reconciliation logic locally, then run the same style of workload on
            remote compute when the dataset becomes too large for a laptop or scheduled
            pipeline worker.
          </p>

          <pre className="blog-code-block">{`expected = (
    pl.scan_parquet("s3://migration/expected/")
    .with_columns(pl.col("balance").round(2))
    .select(["business_key", "status", "balance", "effective_date"])
)

actual = (
    pl.scan_parquet("s3://migration/actual/")
    .with_columns(pl.col("balance").round(2))
    .select(["business_key", "status", "balance", "effective_date"])
)

mismatches = (
    expected.join(actual, on="business_key", how="full", suffix="_actual")
    .filter(
        (pl.col("status") != pl.col("status_actual")) |
        (pl.col("balance") != pl.col("balance_actual")) |
        (pl.col("effective_date") != pl.col("effective_date_actual"))
    )
)`}</pre>

          <h2>Where this fits in a medallion architecture</h2>

          <p>
            In a medallion architecture, reconciliation should not sit outside the platform
            as a one-off spreadsheet exercise. It should become part of the operating
            model.
          </p>

          <div className="blog-layer-grid">
            {architectureLayers.map((item) => (
              <section key={item.layer}>
                <span>{item.layer}</span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </section>
            ))}
          </div>

          <h2>Why this matters</h2>

          <p>
            The future of data testing is executable evidence. For migration teams, that
            means being able to answer whether the record moved correctly, which fields
            changed, whether the change was expected, which dependency caused the mismatch
            and whether the dataset is safe to publish.
          </p>

          <p>
            That is the type of testing layer modern data platforms need. It combines
            Python automation, efficient compute, medallion architecture and governance
            evidence into one delivery pattern.
          </p>

          <section className="blog-sources">
            <h2>Sources and further reading</h2>
            <ul>
              {sourceLinks.map((source) => (
                <li key={source.href}>
                  <a href={source.href} target="_blank" rel="noreferrer">
                    {source.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>

          <footer className="blog-post-footer">
            <p>
              Data Apparatus helps teams design data engineering, migration testing,
              reconciliation and governance patterns that produce evidence, not just
              pipelines.
            </p>
            <Link to="/contact" className="primary-btn">
              Discuss migration testing
            </Link>
          </footer>
        </article>
      </main>

      <Footer />
    </div>
  );
}
